<script lang="ts" setup>
  import { onMounted, ref, defineComponent, defineEmits, nextTick, watch } from 'vue'
  import AppToast from '@/components/AppToast/toast'
  import _fetch from '@/utils/fetch'
  import { useI18n } from 'vue-i18n'
  import { useIsMobile } from '@/hooks/useIsMobile'
  import AppImage from '@/components/AppImage.vue'

  defineComponent({
    name: 'AppUploadLocalImage'
  })

  export interface ImageItem {
    url?: string
    delayIndex: number
    loading?: boolean
    uploadResult?: any
    localUrl: string
    blurValue?: string
    localKey: string
    imageResize512Url?: string // 添加服务器返回的图片URL
  }

  // 定义 props
  interface Props {
    maxCount?: number // 最大上传数量
    maxSize?: number // 最大文件大小（MB）
    accept?: string // 接受的文件类型
    disabled?: boolean // 是否禁用
    uploadUrl?: string // 上传接口地址
    modelValue?: ImageItem[] // 支持双向绑定的图片列表
    topicId?: string // 上传接口附加的 topicId
  }

  const props = withDefaults(defineProps<Props>(), {
    maxCount: 9,
    maxSize: 20,
    accept: 'image/*',
    disabled: false,
    uploadUrl: '/aigc/api/generate/generate-image',
    modelValue: () => [],
    topicId: ''
  })

  const imageInputRef = ref<HTMLInputElement | null>(null)
  const { t } = useI18n()
  const isMobile = useIsMobile()
  const imageList = ref<ImageItem[]>(props.modelValue || [])
  const emit = defineEmits(['changeEvent', 'update:modelValue'])

  // 监听外部传入的 modelValue 变化
  const updateImageList = (newList: ImageItem[]) => {
    imageList.value = newList
    emit('update:modelValue', newList)
    emit('changeEvent', newList)
  }

  // 监听外部 modelValue 变化
  watch(
    () => props.modelValue,
    newValue => {
      if (newValue && newValue !== imageList.value) {
        imageList.value = newValue
      }
    },
    { deep: true }
  )

  const handleDeleteImage = (key: string) => {
    const newList = imageList.value.filter(item => item?.localKey !== key)
    updateImageList(newList)
  }

  // 通过 url 删除图片
  const deleteImageByUrl = (url: string) => {
    const newList = imageList.value.filter(item => item?.url !== url)
    updateImageList(newList)
  }

  // 验证单个文件
  const validateFile = (file: File): boolean => {
    if (file.size > props.maxSize * 1024 * 1024) {
      AppToast({
        type: 'error',
        message: `图像文件超过${props.maxSize}MB，超出部分未上传`
      })
      return false
    }
    if (file.type === 'image/heic' && !isMobile.value) {
      AppToast({ message: '图片格式错误', type: 'warning' })
      return false
    }
    // 过滤GIF格式
    if (file.type === 'image/gif') {
      AppToast({
        type: 'error',
        message: '图片格式不符，未上传'
      })
      return false
    }
    return true
  }

  // 验证图片比例
  const validateImageRatio = (img: HTMLImageElement) => {
    const ratio = img.width / img.height
    if (ratio < 0.5 || ratio > 2) {
      AppToast({
        type: 'error',
        message: t('appGenerator.image_ratio_limit_tip')
      })
      return false
    }
    return true
  }

  // 创建图片预览
  const createImagePreview = (
    file: File,
    index: number,
    startIndex: number
  ): Promise<ImageItem | null> => {
    return new Promise<ImageItem | null>(resolve => {
      const reader = new FileReader()
      reader.onload = e => {
        const imgUrl = e.target?.result as string
        const img = new Image()
        img.src = imgUrl
        img.onload = () => {
          if (validateImageRatio(img)) {
            resolve({
              localUrl: imgUrl,
              // 使用文件名、大小、修改时间和随机数生成唯一key
              localKey: `${file.name}_${file.size}_${file.lastModified}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              delayIndex: startIndex + index,
              loading: true,
              blurValue: '10px' // 初始模糊值
            })
          } else {
            resolve(null)
          }
        }
        img.onerror = () => resolve(null)
      }
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(file)
    })
  }

  // 上传单个文件并处理结果
  const uploadSingleImage = async (file: File, imageItem: ImageItem, index: number) => {
    const startTime = Date.now()
    const maxBlurTime = 5000 // 5秒
    const minBlurValue = '1px' // 最小模糊值
    const maxBlurValue = '10px' // 最大模糊值

    // 开始模糊动画
    const blurInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / maxBlurTime, 1)

      // 计算当前模糊值：从最大模糊值逐渐减少到最小模糊值
      const currentBlur =
        parseFloat(maxBlurValue) - (parseFloat(maxBlurValue) - parseFloat(minBlurValue)) * progress

      // 找到对应的图片项并更新模糊值
      const targetImage = imageList.value.find(item => item?.localKey === imageItem?.localKey)
      if (targetImage) {
        const newBlurValue = `${currentBlur.toFixed(1)}px`
        targetImage.blurValue = newBlurValue
        // 使用 nextTick 来优化性能，避免频繁的响应式更新
        if (elapsed % 500 === 0) {
          // 每500ms才触发一次响应式更新
          nextTick(() => {
            // 触发响应式更新
            imageList.value = [...imageList.value]
          })
        }
      }
    }, 100) // 每100ms更新一次，获得更平滑的效果

    try {
      const formData = new FormData()
      formData.append('imageFile', file)
      formData.append('fileAbsolutePath', 'xpn')
      if (props.topicId) {
        formData.append('topicId', props.topicId)
      }

      const response = await _fetch(props.uploadUrl, {
        method: 'POST',
        body: formData
      })

      const res = await response.json()
      const result = res?.data || {}

      // 清除模糊动画定时器
      clearInterval(blurInterval)

      if (res?.code === 200) {
        // 上传成功
        let dataItem = result?.[0] || {}
        let { status = 0 } = dataItem || {}
        if (status === 0) {
          handleUploadSuccess(result, imageItem, index)
        } else {
          handleUploadError(status, imageItem)
        }
      } else {
        // 上传失败
        handleUploadFailure(result, imageItem)
      }
    } catch (error: any) {
      // 清除模糊动画定时器
      clearInterval(blurInterval)
      // 网络错误
      handleNetworkError(error, imageItem)
    }
  }

  // 处理上传成功
  const handleUploadSuccess = (data: any, imageItem: ImageItem, index: number) => {
    let targetImage: any = imageList.value.find(item => item?.localKey === imageItem?.localKey)
    if (targetImage) {
      if (data.length) {
        let dataItem = data[0] || {}
        let { generateItemDetail } = dataItem || {}
        targetImage.loading = false // 隐藏遮罩层
        targetImage.blurValue = '0px' // 清除模糊
        for (let key in generateItemDetail) {
          if (Object.prototype.hasOwnProperty.call(generateItemDetail, key)) {
            targetImage[key] = generateItemDetail[key]
          }
        }
      } else {
        // 数据为空时也要处理
        targetImage.loading = false
        targetImage.blurValue = '0px'
      }
      emit('changeEvent', imageList.value)
    }
  }

  const handleUploadError = (status: number, failedImageItem: ImageItem) => {
    if (status === 1) {
      AppToast({
        type: 'error',
        message: '图片格式及文件尺寸不符，未上传'
      })
    }
    if (status === 2) {
      AppToast({
        type: 'error',
        message: '您上传的图片包含违规内容，请重新上传'
      })
    }
    // 只移除失败的图片
    const newList = imageList.value.filter(item => item?.localKey !== failedImageItem?.localKey)
    updateImageList(newList)
  }
  // 处理上传失败
  const handleUploadFailure = (result: any, failedImageItem: ImageItem) => {
    AppToast({
      type: 'error',
      message: result?.msg || '图片上传失败，请重试'
    })
    // 只移除失败的图片
    const newList = imageList.value.filter(item => item?.localKey !== failedImageItem?.localKey)
    updateImageList(newList)
  }

  // 处理网络错误
  const handleNetworkError = (error: any, failedImageItem: ImageItem) => {
    AppToast({
      type: 'error',
      message: error?.msg || '图片上传失败，请重试'
    })
    // 移除网络错误的图片
    const newList = imageList.value.filter(item => item?.localKey !== failedImageItem?.localKey)
    updateImageList(newList)
  }

  // 主处理函数
  const handleAddImage = async (event: Event, imageInputRef: HTMLInputElement) => {
    const len = imageList.value.length
    const files = (event.target as HTMLInputElement).files
    if (!files || !imageInputRef) return
    //检查图片是否合规
    let filesArray = Array.from(files).filter(validateFile)

    if (len + files.length > props.maxCount) {
      AppToast({
        type: 'error',
        message: `最多支持发布${props.maxCount}张图片，超出部分未上传`
      })
    }
    filesArray = filesArray.slice(0, props.maxCount - len)

    if (!filesArray.length) return
    // 1. 验证文件
    const validFiles = filesArray.filter(validateFile)
    if (!validFiles.length) {
      // 所有文件都被拦截时，不需要通知外部组件，因为没有变化
      if (imageInputRef) imageInputRef.value = ''
      return
    }
    // 2. 创建预览图片
    const previewPromises = validFiles.map((file, index) => createImagePreview(file, index, len))
    const newImages = await Promise.all(previewPromises)
    const validImages = newImages.filter(Boolean) as ImageItem[]
    // 检查是否有有效的预览图片
    if (!validImages.length) {
      // 所有预览图片都创建失败时，不需要通知外部组件，因为没有变化
      return
    }
    // 3. 添加到列表显示 loading 状态
    const newList = [...imageList.value, ...validImages]
    updateImageList(newList)

    // 4. 为每个图片独立启动上传任务
    validImages.forEach((imageItem, index) => {
      // 找到对应的文件
      const fileIndex = validFiles.findIndex((_, i) => {
        // 通过延迟索引来匹配文件
        return imageItem.delayIndex === len + i
      })
      if (fileIndex !== -1) {
        const file = validFiles[fileIndex]
        // 每个图片独立上传，不等待其他图片
        uploadSingleImage(file, imageItem, index)
      }
    })

    // 5. 重置input的value，确保相同文件也能触发change事件
    if (imageInputRef) {
      imageInputRef.value = ''
    }
  }
  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    const files = event.dataTransfer?.files
    if (files && imageInputRef.value) {
      // 模拟 input change 事件
      const fakeEvent = {
        target: { files }
      } as unknown as Event
      handleAddImage(fakeEvent, imageInputRef.value)
    }
  }

  // 暴露方法给外部使用
  defineExpose({
    deleteImageByKey: handleDeleteImage,
    deleteImageByUrl: deleteImageByUrl,
    clearAllImages: () => updateImageList([]),
    getImageList: () => imageList.value
  })
</script>
<template>
  <div class="generator-upload-wrapper" ref="generatorResult">
    <div class="generator-upload-content">
      <div class="generator-upload-list-wrap" v-if="imageList.length > 0">
        <div class="generator-upload-list">
          <label
            class="generator-upload-footer-label"
            @drop="handleDrop"
            @dragover.prevent
            @dragenter.prevent
            v-if="imageList.length < props.maxCount"
          >
            <div class="generator-upload-item">
              <span class="generator-upload-item-icon hitems-Vector"></span>
              <span class="generator-upload-item-text">上传图片</span>
              <input
                class="image-input"
                :class="{ disabled: props.disabled }"
                type="file"
                :accept="props.accept"
                @change="e => handleAddImage(e, imageInputRef as HTMLInputElement)"
                ref="imageInputRef"
                multiple
                :disabled="props.disabled"
              />
            </div>
          </label>
          <div
            class="generator-upload-item"
            :style="{ '--animation-delay': `${index * 0.1}s` }"
            v-for="(item, index) in imageList"
            :key="item?.localKey"
          >
            <AppImage
              v-if="item?.loading"
              :src="item?.localUrl"
              alt="本地上传图片"
              :fullscreen="item?.localUrl"
            />
            <AppImage
              v-else
              :src="item?.imageResize512Url || item?.localUrl"
              alt="本地上传图片"
              :fullscreen="item?.localUrl"
            />
            <!-- 高斯模糊遮罩 -->
            <div
              v-if="item.loading"
              class="blur-mask"
              :style="{ '--blur-value': item.blurValue || '10px' }"
            >
              <div class="blur-content">
                <div class="loading-spinner"></div>
                <span class="loading-text">上传中...</span>
              </div>
            </div>
            <div class="delete-item-icon" v-if="!item.loading">
              <span
                class="delete-item-icon-btn hitems-guanbi"
                @click="handleDeleteImage(item?.localKey)"
              ></span>
            </div>
          </div>
        </div>
      </div>
      <div class="generator-upload-none-image-wrap" v-else>
        <label
          class="generator-upload-footer-label"
          @drop="handleDrop"
          @dragover.prevent
          @dragenter.prevent
        >
          <div class="generator-upload-none-image">
            <span class="generator-upload-footer-icon hitems-Vector"></span>
            <span class="generator-upload-footer-text">拖拽图片到此或点击上传</span>
            <span class="generator-upload-footer-text-desc">
              支持 JPG/PNG/JPEG/WEBP，图片小于 {{ props.maxSize }} MB，最多 {{ props.maxCount }} 张
            </span>
            <input
              class="image-input"
              :class="{ disabled: props.disabled }"
              type="file"
              :accept="props.accept"
              @change="e => handleAddImage(e, imageInputRef as HTMLInputElement)"
              ref="imageInputRef"
              multiple
              :disabled="props.disabled"
            />
          </div>
        </label>
      </div>
    </div>
  </div>
</template>
<style lang="stylus" scoped>
  @import './index.styl';
</style>
